import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  } from 'chart.js';
  import {ReactComponent as Segmentation_people} from '../Layer_images/Settings_images/segmentation_people.svg'
  import {UI_Table} from './ui-table';

  
  // Register Chart.js components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

export function SegmentationPopupDemographic({segmentationMain, segmentData, region_mapping}) {

    const data = {
        labels: ['Gender Distribution'],
        datasets: [
        {
            label: 'Male',
            data: [segmentData?.gender?.male],
            backgroundColor: 'rgba(173, 216, 230, 1)', // Light blue
            borderColor: 'rgba(173, 216, 230, 1)',
            borderWidth: 1,
            barPercentage: 1,
            categoryPercentage: 1,
        },
        {
            label: 'Female',
            data: [segmentData?.gender?.female],
            backgroundColor: 'rgba(30, 144, 255, 1)', // Darker blue
            borderColor: 'rgba(30, 144, 255, 1)',
            borderWidth: 1,
            barPercentage: 1,
            categoryPercentage: 1,
        },
        ],
    };

    // Chart options
    const optionsGender = {
        indexAxis: 'y', // Horizontal bar chart
        scales: {
        x: {stacked: true, grid: { display: false, }, ticks: { display: false, }, border: { display: false, }},
        y: { stacked: true, grid: { display: false, }, ticks: { display: false}, border: { display: false, }},
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
        legend: {
            position: 'top',
            align: 'end',
            labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
            },
        },
        tooltip: {
            enabled: false,
        },
        datalabels: {
            display: true,
            color: (context) => context.datasetIndex === 0 ? '#555' : 'white',
            formatter: (value) => `${value}%`,
            font: {
            weight: 'bold',
            },
            align: 'center',
            anchor: 'center',
        },
        },
    };

    const formatRaceLabel = (race) => {
        const labels = {
        white: "White",
        black: "Black/African American",
        asian: "Asian",
        hispanic: "Hispanic/Latino",
        other: "Other"
        };
        return labels[race] || race.charAt(0).toUpperCase() + race.slice(1);
    };
    const race = segmentData.race ? segmentData.race : {}
    // Create sorted entries from highest to lowest percentage
    const raceSortedEntries = Object.entries(race)
        .sort((a, b) => b[1] - a[1]);

    const raceChartData = {
        labels: raceSortedEntries.map(([race]) => formatRaceLabel(race)),
        datasets: [
        {
            data: raceSortedEntries.map(([_, value]) => value),
            backgroundColor: '#3b82f6', 
            barThickness: 20,
            borderRadius: 4,
        },
        ],
    };

    const formatEducationLabel = (key) => {
        const labels = {
        "less_than_hs": "Less than 9th grade",
        "hs_graduate": "High-school graduate",
        "some_college_aa": "Some college",
        "bachelors": "Bachelors degree",
        "graduate_professional": "Graduate or professional degree"
        };
        return labels[key] || key;
    };

    // Define a blues-to-orange color scheme
    const colorScheme = [
        'rgba(173, 216, 230, 1)',   // Light blue
        'rgba(91, 155, 213, 1)',     // Medium blue
        'rgba(220, 104, 3, 1)',      // Dark blue
        'rgba(255, 217, 102, 1)',    // Light gold
        'rgba(254, 200, 75, 1)',     // Gold
        'rgba(247, 144, 9, 1)',     // Orange
        'rgba(168, 88, 45, 1)'       // Brown
    ];
    const education = segmentData.education ? segmentData.education : {}
    // Convert the data to Chart.js format
    const educationChartData = {
        labels: Object.keys(education).map(key => formatEducationLabel(key)),
        datasets: [
        {
            data: Object.values(education),
            backgroundColor: colorScheme.slice(0, Object.keys(education).length),
            borderWidth: 0,
        },
        ],
    };
    const occupation = Object.entries(segmentData.occupation).map(([occupation, value]) => ({
        occupation: occupation,
        percentage: `${value}%`
      }));


    const calculateTopExpensePercentages = (expenses, totalKey = 'Total_Expenditure') => {
        // Make sure we have valid data
        if (!expenses || !expenses[totalKey]) {
        return {};
        }
    
        const totalExpenditure = expenses[totalKey];
        
        // Create array of [key, value] pairs, excluding the total key
        const expensePairs = Object.entries(expenses)
        .filter(([key]) => key !== totalKey)
        // Sort by value in descending order
        .sort((a, b) => b[1] - a[1])
        // Take the top 8 expenses
        .slice(0, 6)
        // Convert to percentage
        .map(([key, value]) => [
            key, 
            Math.round((value / totalExpenditure) * 1000) / 10 
        ]);
        
        // Convert back to object
        return Object.fromEntries(expensePairs);
    };
    const expenseLabelMapping = {
        "Total_Expenditure": "Total Expenditure",
        "Food_Total": "Food (Total)",
        "Food_At_Home": "Food at Home",
        "Food_Away_From_Home": "Food Away from Home",
        "Housing Total": "Housing (Total)",
        "Shelter": "Shelter",
        "Rent": "Rent",
        "Utilities Total": "Utilities (Total)",
        "Natural Gas": "Natural Gas",
        "Electricity": "Electricity",
        "Telephone": "Telephone",
        "Water Sewage": "Water & Sewage",
        "Transportation Total": "Transportation (Total)",
        "Gasoline": "Gasoline",
        "Public Transportation": "Public Transportation",
        "Healthcare Total": "Healthcare (Total)",
        "Medical Services": "Medical Services",
        "Prescription Drugs": "Prescription Drugs",
        "Entertainment": "Entertainment",
        "TV Radio": "TV & Radio",
        "Personal Care": "Personal Care",
        "Reading": "Reading",
        "Education": "Education",
        "Apparel": "Apparel",
        "Alcohol": "Alcohol",
        "Tobacco": "Tobacco",
        "Miscellaneous": "Miscellaneous"
    };
    
    // Function to format expense labels
    const formatExpenseLabel = (key) => {
        return expenseLabelMapping[key] || key.replace(/_/g, ' ');
    };
    // Usage in your existing code
    const expenses = segmentData.expenses ? segmentData.expenses : {};
    const topExpensesPercentages = calculateTopExpensePercentages(expenses);
    
    const expenseChartData = {
        labels: Object.keys(topExpensesPercentages).map(key => formatExpenseLabel(key)),
        datasets: [
        {
            data: Object.values(topExpensesPercentages),
            backgroundColor: colorScheme.slice(0, Object.keys(topExpensesPercentages).length),
            borderWidth: 0,
        },
        ],
    };

    const optionsEducation = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '50%', // Adjusts the size of the hole in the middle
        plugins: {
        legend: {
            position: 'right',
            align: 'center',
            labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 15,
            font: {
                size: 12,
            },
            generateLabels: (chart) => {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                    const meta = chart.getDatasetMeta(0);
                    const style = meta.controller.getStyle(i);
                    
                    return {
                    text: label,
                    fillStyle: style.backgroundColor,
                    strokeStyle: style.borderColor,
                    lineWidth: style.borderWidth,
                    pointStyle: 'circle',
                    hidden: false,
                    index: i
                    };
                });
                }
                return [];
            }
            }
        },
        tooltip: {
            callbacks: {
            label: function(context) {
                const label = context.label || '';
                const value = context.raw || 0;
                return `${label}: ${value}%`;
            }
            }
        }
        }
    };

    const optionsRace = {
        indexAxis: 'y',
        scales: {
        x: {
            beginAtZero: true,
            max: 100,
            grid: {
            display: false,
            },
            ticks: {
            display: false,
            },
            border: {
            display: false,
            },
        },
        y: {
            grid: {
            display: false,
            },
            border: {
            display: false,
            },
        },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            enabled: false,
        },
        },
        layout: {
        padding: 0,
        },
    };
        
    function formatNumber(value) {
        // Check if the value is a number
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
          // Convert to number to handle string numbers, then to string for formatting
          return Number(value).toLocaleString();
        } else {
          // Return the original value if it's not a number
          return value;
        }
      }
        
     return   <div>
          <div className='display-flex gap-10  mtb-10 sidbar-primary-text'>

            <div className="box-container" style={{flex:1}}>
                <div className='segmentation-top-sidebar'>
                  <div className='display-inline-block pt-5'><Segmentation_people/></div>
                  <div className='display-inline-block font-size-10'>Average Family Size</div>
                </div>
                <div className='text-align-left pl-5' >{formatNumber(segmentData.household_size)}</div>
            </div>
            <div className="box-container" style={{flex:1}}>
                <div className='segmentation-top-sidebar'>
                  <div className='display-inline-block pt-5'>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.36 4.54679L9.51999 1.86012C8.47333 1.12679 6.86666 1.16679 5.85999 1.94679L2.51999 4.55345C1.85333 5.07345 1.32666 6.14012 1.32666 6.98012V11.5801C1.32666 13.2601 2.70666 14.6668 4.40666 14.6668H11.5933C13.2933 14.6668 14.6733 13.2868 14.6733 11.5868V7.06679C14.6733 6.16679 14.0933 5.06012 13.36 4.54679ZM8.49999 12.0001C8.49999 12.2735 8.27333 12.5001 7.99999 12.5001C7.72666 12.5001 7.49999 12.2735 7.49999 12.0001V10.0001C7.49999 9.72679 7.72666 9.50012 7.99999 9.50012C8.27333 9.50012 8.49999 9.72679 8.49999 10.0001V12.0001Z" fill="#A4A7AE"/>
                    </svg>
                  </div>
                  <div className='display-inline-block font-size-10'>Urban Type</div>
                </div>
                <div className='text-align-left pl-5'>{region_mapping[segmentationMain.region_type]}</div>
            </div>
            <div className="box-container" style={{flex:2, height: 60, paddingRight: 10}}>
              <div >
                <div className='segmentation-top-sidebar' style={{position:'absolute'}}>
                    <div className='display-inline-block pt-5' >
                    <svg height="16px" width="16px" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#A4A7AE">
                        <g id="SVGRepo_iconCarrier"><g><path d="M474.234,284.88c23.29-23.268,37.781-55.654,37.766-91.185c0.015-35.53-14.476-67.917-37.766-91.178 c-23.261-23.298-55.64-37.788-91.185-37.766c-35.53-0.022-67.924,14.469-91.17,37.766c-23.304,23.261-37.795,55.648-37.78,91.178 c-0.015,35.53,14.476,67.916,37.78,91.185c19.268,19.297,44.809,32.517,73.21,36.488v52.976h-58.486v35.944h58.486v56.766h35.937 v-56.766h58.486v-35.944h-58.486v-52.976C429.426,317.397,454.966,304.177,474.234,284.88z M383.049,275.921 c-11.413-0.008-22.172-2.301-32.002-6.454c-14.724-6.222-27.327-16.684-36.198-29.817c-8.857-13.147-14.012-28.844-14.012-45.955 c0-11.42,2.294-22.172,6.447-31.994c6.214-14.731,16.669-27.326,29.81-36.198c13.155-8.864,28.85-14.019,45.955-14.034 c11.413,0.008,22.172,2.302,32.002,6.462c14.724,6.214,27.327,16.676,36.198,29.809c8.857,13.156,14.012,28.851,14.026,45.956 c-0.014,11.42-2.308,22.172-6.461,31.995c-6.214,14.73-16.683,27.333-29.81,36.198 C415.849,270.759,400.168,275.907,383.049,275.921z" /><path d="M146.912,202.305v-88.55l35.69,35.683l25.424-25.41l-79.075-79.082l-79.09,79.082l25.41,25.41l35.704-35.69 v88.557c-28.401,3.978-53.941,17.199-73.209,36.502C14.462,262.069-0.014,294.448,0,329.979 c-0.014,35.545,14.462,67.924,37.766,91.184c23.261,23.305,55.64,37.788,91.185,37.774c35.53,0.014,67.91-14.469,91.17-37.774 c23.304-23.261,37.78-55.64,37.78-91.184c0-35.53-14.476-67.91-37.78-91.171C200.854,219.505,175.313,206.283,146.912,202.305z M204.716,361.981c-6.229,14.73-16.683,27.326-29.81,36.198c-13.154,8.872-28.85,14.027-45.955,14.027 c-11.427,0-22.172-2.294-32.002-6.447c-14.723-6.229-27.327-16.684-36.198-29.817c-8.872-13.155-14.026-28.844-14.026-45.962 c0-11.412,2.294-22.164,6.446-31.987c6.229-14.723,16.684-27.326,29.824-36.198c13.14-8.864,28.836-14.011,45.955-14.026 c11.413,0,22.157,2.294,32.002,6.454c14.709,6.222,27.312,16.676,36.184,29.809c8.871,13.156,14.026,28.844,14.026,45.948 C211.163,341.405,208.868,352.165,204.716,361.981z" /></g></g>
                      </svg>
                    </div>
                  <div className='display-inline-block font-size-10'>Gender Distribution</div>

                  </div>
              </div>
              <Bar data={data} options={optionsGender} />
            </div>
          </div>
              <div className='display-flex gap-10  mtb-10 sidbar-primary-text mt-20'>
                <div className="box-container" style={{flex:1, padding: 10}}>
                  <div className='text-align-left pl-5 mb-10'>Race Breakdown</div>
                    <div style={{height: 260}}>
                      <Bar data={raceChartData} options={optionsRace} />
                    </div>
                  </div>
                  <div className="box-container" style={{flex:1, padding: 10}}>
                    <div className='text-align-left pl-5 mb-10'>Education Breakdown</div>
                    <div style={{height: 260}}>

                      <Doughnut data={educationChartData} options={optionsEducation} />
                      </div>
                  </div>

              </div>
              <div className='display-flex gap-10  mtb-10 sidbar-primary-text mt-20'>
                <div className="box-container" style={{flex:1, padding: 10}}>
                  <div className='text-align-left pl-5 mb-10'>Expense Breakdown</div>
                  <div style={{height: 260}}>
                    <Doughnut data={expenseChartData} options={optionsEducation} />
                  </div>
                  </div>
                  <div className="box-container" style={{flex:1, padding: 10}}>
                    <div className='text-align-left pl-5 mb-10'>Occupation List</div>
                    <UI_Table rows={occupation} titles={[{title: 'Occupation Name', key:'occupation'}, {title: 'Percentage', key: 'percentage'}]}/>
                  </div>

              </div>
        </div>
}